import React from "react";
import { useNavigate } from "react-router-dom";

import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import {
  findExistingProtocolForOfficialMatch,
  findSavedProtocolForOfficialMatch,
} from "@/lib/protocolMatches";
import { useOpenMatches, useUserMatches } from "@/queries/matches";

export function useOpenOfficialMatch({ enabled = true } = {}) {
  const navigate = useNavigate();
  const { requestOfficialShell, requestProtocolSession } = useProtocolShell();
  const {
    data: createdMatches = [],
    isFetched: createdMatchesFetched,
    refetch: refetchCreatedMatches,
  } = useOpenMatches({
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const {
    data: savedMatches = [],
    isFetched: savedMatchesFetched,
    refetch: refetchSavedMatches,
  } = useUserMatches({
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return React.useCallback(async (officialId) => {
    if (!enabled || !officialId) return;
    let resolvedCreatedMatches = createdMatches;
    let resolvedSavedMatches = savedMatches;
    try {
      [resolvedCreatedMatches, resolvedSavedMatches] = await Promise.all([
        createdMatchesFetched
          ? createdMatches
          : refetchCreatedMatches().then((result) => result.data || []),
        savedMatchesFetched
          ? savedMatches
          : refetchSavedMatches().then((result) => result.data || []),
      ]);
    } catch {
      // Opening the official match card remains a safe fallback if protocol lookup fails.
    }
    const savedProtocol = findSavedProtocolForOfficialMatch(resolvedSavedMatches, officialId);
    if (savedProtocol?.id) {
      navigate(`/account/matches/saved/${savedProtocol.id}`);
      return "saved_protocol";
    }

    const existingMatch = findExistingProtocolForOfficialMatch(resolvedCreatedMatches, officialId);
    if (existingMatch?.id) {
      requestProtocolSession(existingMatch.id);
      return "open_protocol";
    }
    requestOfficialShell(officialId);
    return "official_match";
  }, [
    createdMatches,
    createdMatchesFetched,
    enabled,
    navigate,
    refetchCreatedMatches,
    refetchSavedMatches,
    requestOfficialShell,
    requestProtocolSession,
    savedMatches,
    savedMatchesFetched,
  ]);
}
